import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { CategoryTabs } from '@/components/home/category-tabs'
import { PostCard } from '@/components/home/post-card'
import { SchoolSelector } from '@/components/home/school-selector'
import { SkeletonList } from '@/components/ui/skeleton-card'
import { useContentStore } from '@/stores/content-store'
import { useAuthStore } from '@/stores/auth-store'
import { PageTransition, EmptyState } from '@/components/ui/ux-enhancer'
import { useToast } from '@/components/ui/toast-provider'
import { TrendingUp, Search } from 'lucide-react'

export default function TrendingPage() {
  const navigate = useNavigate()
  const [selectedCategory, setSelectedCategory] = useState('全部')
  const [selectedSchool, setSelectedSchool] = useState('全部学校')
  const { posts, fetchPosts, isLoading } = useContentStore()
  const { user } = useAuthStore()
  const { addToast } = useToast()

  // 处理分类切换
  const handleCategoryChange = (category: string) => {
    setSelectedCategory(category)
  }

  useEffect(() => {
    // 热门榜：后端按点赞数排序（popular）
    fetchPosts({ sort: 'popular' })
  }, [fetchPosts])

  // 时间格式化函数
  function formatTime(dateString: string): string {
    const now = new Date()
    const postTime = new Date(dateString)
    const diffInMinutes = Math.floor((now.getTime() - postTime.getTime()) / (1000 * 60))
    
    if (diffInMinutes < 1) return '刚刚'
    if (diffInMinutes < 60) return `${diffInMinutes}分钟前`
    
    const diffInHours = Math.floor(diffInMinutes / 60)
    if (diffInHours < 24) return `${diffInHours}小时前`
    
    const diffInDays = Math.floor(diffInHours / 24)
    if (diffInDays < 7) return `${diffInDays}天前`
    
    return postTime.toLocaleDateString('zh-CN')
  }

  // 热门内容（后端已按热度排序，取前 50 条）
  const trendingPosts = posts
    .map(post => ({
      id: post.id,
      userId: post.userId,
      author: {
        name: post.isAnonymous ? '匿名用户' : (post.user?.username || '未知用户'),
        avatar: post.isAnonymous ? '' : (post.user?.avatar || ''),
        school: post.school,
        level: post.user?.level || 1
      },
      user: {
        name: post.isAnonymous ? '匿名用户' : (post.user?.username || '未知用户'),
        avatar: post.isAnonymous ? '' : (post.user?.avatar || ''),
        school: post.school,
        level: post.user?.level || 1
      },
      content: post.content,
      title: post.title,
      images: post.images || [],
      category: post.category,
      createdAt: new Date(post.createdAt),
      time: formatTime(post.createdAt),
      likes: post.likes || 0,
      comments: post.comments || 0,
      location: post.location,
      price: post.price,
      condition: post.condition,
      tags: post.tags || [],
      isHot: true,
      hot: true
    }))
    .sort((a, b) => b.likes - a.likes) // 按点赞数排序

  // 筛选帖子
  const filteredPosts = trendingPosts.filter(post => {
    // 分类筛选逻辑
    const categoryMatch = selectedCategory === '全部' || post.category === selectedCategory
    
    // 学校筛选逻辑
    const schoolMatch = selectedSchool === '全部学校' || 
                       post.author?.school === selectedSchool || 
                       post.user?.school === selectedSchool
    
    return categoryMatch && schoolMatch
  })

  if (isLoading) {
    return (
      <PageTransition>
        <div className="space-y-4 lg:space-y-6">
          {/* 页面标题 */}
          <div className="flex items-center space-x-3 mb-6">
            <div className="p-3 bg-gradient-to-r from-orange-500 to-red-500 rounded-xl">
              <TrendingUp className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">热门动态</h1>
              <p className="text-gray-600">发现校园最受关注的内容</p>
            </div>
          </div>

          {/* 校区选择器 */}
          <div className="mb-4">
            <SchoolSelector 
              selectedSchool={selectedSchool}
              onSchoolChange={setSelectedSchool}
            />
          </div>
          
          <CategoryTabs 
            selectedCategory={selectedCategory}
            onCategoryChange={handleCategoryChange}
            showAsLinks={false}
          />

          {/* 骨架屏加载状态 */}
          <SkeletonList count={5} />
        </div>
      </PageTransition>
    )
  }

  return (
    <PageTransition>
      <div className="space-y-4 lg:space-y-6">
        {/* 页面标题 */}
        <div className="flex items-center space-x-3 mb-6">
          <div className="p-3 bg-gradient-to-r from-orange-500 to-red-500 rounded-xl">
            <TrendingUp className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">热门动态</h1>
            <p className="text-gray-600">发现校园最受关注的内容</p>
          </div>
        </div>

        {/* 校区选择器 */}
        <div className="mb-4">
          <SchoolSelector 
            selectedSchool={selectedSchool}
            onSchoolChange={setSelectedSchool}
          />
        </div>
        
        <CategoryTabs 
          selectedCategory={selectedCategory}
          onCategoryChange={handleCategoryChange}
          showAsLinks={false}
        />

        {/* 内容区域 */}
        <div className="space-y-4 lg:space-y-6">
          {filteredPosts.length > 0 ? (
            filteredPosts.map(post => (
              <PostCard key={post.id} post={post} />
            ))
          ) : (
            <EmptyState
              icon={<TrendingUp className="w-16 h-16 text-gray-300" />}
              title="暂无热门内容"
              description="当前筛选条件下没有找到热门内容，试试其他分类或学校吧"
              action={
                <button
                  onClick={() => {
                    navigate('/')
                    addToast({
                      type: 'info',
                      title: '跳转成功',
                      description: '查看更多校园动态'
                    })
                  }}
                  className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
                >
                  查看全部动态
                </button>
              }
            />
          )}
        </div>
      </div>
    </PageTransition>
  )
}